import React from 'react';
import PropTypes from 'prop-types';
import { fleetPanelData, hasInventory } from '../inventory/fleetPanelData';
import { fleetSize, formatDate, formatDateTime, relativeTime } from './projectListModel';
import { Owner, ReviewBar, StatePill } from './ProjectAtoms';

const MAX_VERSIONS = 6;

// Inline details shown below an expanded row or card row (STRY0038867).
const ProjectDetail = ({ project, ctx, canModify, actions }) => {
  const drift = fleetPanelData(project);
  const versions = project.versions || [];
  const label = id => ctx.versionLabel(project, id);

  return (
    <div className="pl-x" onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
      <div className="pl-x__cols">
        <section className="pl-x__col">
          <div className="pl-x__title">Summary</div>
          <dl className="pl-x__dl">
            <dt>Baseline → target</dt>
            <dd className="bz-mono">
              {label(project.versionId)} <span className="pl-vers__arrow">→</span>{' '}
              {project.acceptedVersionId ? (
                <span className="pl-x__ok">{label(project.acceptedVersionId)}</span>
              ) : (
                <span className="bz-muted">none accepted</span>
              )}
            </dd>
            <dt>Fleet</dt>
            <dd>
              {hasInventory(project) ? (
                <>
                  <b>{fleetSize(project)}</b> CIs · snapshot {relativeTime(project.inventorySnapshot.fetchedAt)}
                </>
              ) : (
                <span className="bz-muted">Manual project — no inventory</span>
              )}
            </dd>
            <dt>Created</dt>
            <dd>
              <Owner userId={project.userId} currentUserEmail={ctx.currentUserEmail} />
              <span className="pl-dotsep">·</span>
              <span title={formatDateTime(project.createdAt)}>{formatDate(project.createdAt)}</span>
            </dd>
            <dt>Last activity</dt>
            <dd title={formatDateTime(project.lastActivityAt || project.updatedAt)}>
              {relativeTime(project.lastActivityAt || project.updatedAt)}
            </dd>
          </dl>
          {drift && drift.hasDrift && (
            <div className="pl-x__drift">
              <b>Fleet drift</b> — this project reviews {label(drift.baseline)}; your inventory&apos;s majority is now{' '}
              {drift.majorityVersion}.
            </div>
          )}
        </section>

        <section className="pl-x__col">
          <div className="pl-x__title">Opened versions</div>
          <ReviewBar project={project} available={ctx.availableVersions(project)} />
          {versions.length ? (
            <div className="pl-x__vers">
              {versions.slice(0, MAX_VERSIONS).map(v => (
                <div key={v.versionId} className="pl-x__ver">
                  <span className="bz-mono" title={v.versionId}>
                    {label(v.versionId)}
                  </span>
                  <StatePill state={v.state} />
                </div>
              ))}
              {versions.length > MAX_VERSIONS && (
                <span className="bz-muted">+{versions.length - MAX_VERSIONS} more</span>
              )}
            </div>
          ) : (
            <div className="bz-muted pl-x__empty">No versions opened yet — open the project to start reviewing.</div>
          )}
        </section>
      </div>

      <div className="pl-x__actions">
        {canModify && hasInventory(project) && (
          <button type="button" className="bz-btn bz-btn--sm" onClick={() => actions.refresh(project)}>
            Refresh inventory
          </button>
        )}
        <button type="button" className="bz-btn bz-btn--sm" onClick={() => actions.exportReport(project)}>
          Export report
        </button>
        <span className="bz-grow" />
        <button type="button" className="pl-linkbtn" onClick={actions.collapse}>
          Collapse
        </button>
        <button type="button" className="bz-btn bz-btn--sm bz-btn--primary" onClick={() => actions.open(project)}>
          Open project →
        </button>
      </div>
    </div>
  );
};

ProjectDetail.propTypes = {
  project: PropTypes.object.isRequired,
  ctx: PropTypes.object.isRequired,
  canModify: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired
};

export default ProjectDetail;
